import axios from 'axios'
import _ from 'lodash'
import { MapBrowserEvent } from 'ol'
import { Coordinate } from 'ol/coordinate'
import { Extent } from 'ol/extent'
import Map from 'ol/Map'

import 'ol/ol.css'
import { fromLonLat } from 'ol/proj'
import TileWMS from 'ol/source/TileWMS'
import React, { ComponentType, useState } from 'react'
import { Button, Popup } from 'semantic-ui-react'
import { MAP_PROJECTION } from '../../environment'
import { INITIAL_POS, ZOOM_TO_ZOOM_LEVEL } from './map.constants'
import { useMap } from './map.hook'
import { MapUtils } from './map.utils'
import './MapComponent.css'

export type MapConfigComponentProps = {
  map: Map | null,
  zoomTo: (location: LocationData) => void,
  zoomToExtent?: (extent: Extent) => void,
  setReturnLocation: (location: LocationData) => void,
  onSingleClick: (evt: MapBrowserEvent<UIEvent>, wmsSource: TileWMS) => void
}

export type MapConfigComponent = ComponentType<MapConfigComponentProps>

type props = {
  mapConfigComponent: MapConfigComponent
}

export interface LocationData {
  longitude: number | string,
  latitude: number | string
}

const MapComponent = ({ mapConfigComponent: MapConfigComponent }: props) => {

  const { map, ref, view } = useMap()

  const [recenterLocation, setRecenterLocationInternal] = useState<Coordinate>()
  const [center, setCenter] = useState<Coordinate | undefined>(INITIAL_POS)

  if (map) {
    map.on('moveend', () => {
      setCenter(view.getCenter())
    })
  }

  const showRecenterButton = React.useMemo(() => {
    return (recenterLocation && center && (recenterLocation[0] !== center[0] || recenterLocation[1] !== center[1]))
  }, [center, recenterLocation])

  const recentreMap = () => {
    if (recenterLocation && !_.isEmpty(recenterLocation)) {
      view.animate({
        center: recenterLocation,
      })
    }
  }

  const setRecentreLocation = React.useCallback((location: LocationData) => {
    if (location.latitude || location.longitude) {
      const pos = fromLonLat(MapUtils.longLatToCoord(location.longitude, location.latitude))
      setRecenterLocationInternal(pos)
    } else {
      setRecenterLocationInternal(undefined)
    }
  }, [])

  const zoomTo = React.useCallback((location: LocationData) => {
    if (!_.isEmpty(location)
      && location.longitude
      && location.latitude) {
      // center on the location
      const pos = fromLonLat(MapUtils.longLatToCoord(location.longitude, location.latitude))

      view.cancelAnimations()
      view.animate({
        center: pos,
        zoom: ZOOM_TO_ZOOM_LEVEL,
      })
    }
  }, [view])

  const zoomToExtent = React.useCallback((extent: Extent) => {
    if (extent) {
      const zoom = view.getZoomForResolution(view.getResolutionForExtent(extent))

      view.cancelAnimations()
      view.animate({
        center: getCenterOfExtent(extent),
        zoom: zoom,
      })
    }
  }, [view])

  const getCenterOfExtent = (extent: Extent) => {
    const x = extent[0] + (extent[2] - extent[0]) / 2
    const y = extent[1] + (extent[3] - extent[1]) / 2
    return [x, y]
  }

  const onSingleClick = async (evt: MapBrowserEvent<UIEvent>, wmsSource: TileWMS) => {
    const viewResolution = /** @type {number} */ (view.getResolution())
    if (wmsSource) {
      const url = wmsSource.getFeatureInfoUrl(
        evt.coordinate,
        viewResolution ? viewResolution : 2000,
        MAP_PROJECTION,
        {
          'INFO_FORMAT': 'application/json',
        },
      )

      if (url) {
        const response = await axios.create().get(url)
        const placeObj = response.data
        if (placeObj && placeObj.features && placeObj.features.length > 0) {
          return placeObj.features[0]
        }
      }
    }
  }

  return (
    <>
      <div ref={ref} id="mapComponent" className="map"/>
      {MapConfigComponent &&
          <MapConfigComponent
              map={map ?? null}
              zoomTo={zoomTo}
              zoomToExtent={zoomToExtent}
              setReturnLocation={setRecentreLocation}
              onSingleClick={onSingleClick}
          />}
      {showRecenterButton &&
          <div className="button-recentre">
              <Popup content="RE-CENTRE"
                     trigger={<Button placeholder="Re-centre"
                                      color="blue"
                                      size="tiny"
                                      icon="location arrow"
                                      onClick={recentreMap}/>
                     }/>
          </div>}
    </>
  )
}

export default MapComponent